import { useState, useEffect, useRef } from "react";
import {
  getGrades,
  getSections,
  getBilletQualityStatuses,
} from "../../repository/BilletYardInventoryRepository";
import {
  DropdownItem,
  extractRows,
  filterUnknownOnly,
} from "../../components/modals/search-modal/utils/searchDataUtils";
import type { ResponseMESF } from "@dexteel/mesf-core";

interface SelectionItem {
  Value: string;
  Title: string;
  [key: string]: unknown; // Allow additional properties to match shared interface
}

export const useLocationContentData = () => {
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  const [grades, setGrades] = useState<SelectionItem[]>([]);
  const [sections, setSections] = useState<SelectionItem[]>([]);
  const [qualityStatuses, setQualityStatuses] = useState<SelectionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(true);
          setError(null);
        }

        const [gradesData, sectionsData, qualityData] = await Promise.all([
          getGrades(),
          getSections(),
          getBilletQualityStatuses(),
        ]);

        // Using imported utility functions

        // Process data into SelectionItem format
        const processData = (apiResponse: ResponseMESF): SelectionItem[] => {
          try {
            const rawRows = extractRows(apiResponse);
            const filteredRows = filterUnknownOnly(rawRows, "processData");

            return filteredRows.map((item: DropdownItem) => ({
              Value: String(item?.Value || item?.Code || item?.ID || ""),
              Title: String(
                item?.Title ||
                  item?.Name ||
                  item?.Display ||
                  item?.Value ||
                  item?.Code ||
                  "",
              ),
            }));
          } catch (error) {
            console.error("Error processing API data:", error);
            return [];
          }
        };

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setGrades(processData(gradesData));
          setSections(processData(sectionsData)); // Filter Unknown for sections (they can't be used for adding billets)
          setQualityStatuses(processData(qualityData));
        }
      } catch (err) {
        console.error("Failed to fetch location content data:", err);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : "Failed to fetch data");
          // Set empty arrays on error to prevent crashes
          setGrades([]);
          setSections([]);
          setQualityStatuses([]);
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  // Cleanup effect to track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    grades,
    sections,
    qualityStatuses,
    isLoading,
    error,
  };
};
